import type { MouseEvent, ReactNode } from 'react'
import {
  AnimationHandler,
  Badge,
  Nameplate,
  Tile,
  TileCaption,
  TileImage,
  TileOverlay,
  HoverHandler,
  Button,
  IconPlayDefault,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import Link from 'components/Link'
import { SeriesLogo } from 'components/SeriesLogo'
import { CourseSubType } from 'types/data'
import { useVideoContext } from '../VideoContext'
import { trackWatchTrailerClick } from '../events'
import { ClassTileContext, useClassTile } from './ClassTileContext'
import { ClassTileProps } from './types'
import {
  tileOptimizedImage,
  nameplateOptimizedImage,
  getDuration,
} from './utils'

// This extends the `img` attributes TS interface to allow use of a `fetchpriority` attribute
// The types support `fetchPriority` (camelCase) but there's a bug in React that raises an error with this naming
// It's not clear to me what combination of React/Next versions actually fix this issue
// https://github.com/vercel/next.js/issues/65161
declare module 'react' {
  interface HTMLAttributes<T>
    extends React.AriaAttributes,
      React.DOMAttributes<T> {
    fetchpriority?: 'high' | 'low' | 'auto'
  }
}

const ClassTileTag = () => {
  const { tile } = useClassTile()
  const tag = tile.releaseDescription ?? tile.tag

  if (!tag) {
    return null
  }

  return (
    <Badge>
      <span className='mc-text--capitalize'>{tag}</span>
    </Badge>
  )
}

// AnimationHandler manages animation via `className`,
// so we need to pass it down to the TileImage to animate it correctly
const ClassTileImage = ({ className }: { className?: string }) => {
  const { tile, isPrioritized } = useClassTile()

  return (
    <TileImage
      className={className}
      image={
        // explicitly use a standard `img` element so we can deterministically preload images
        // eslint-disable-next-line @next/next/no-img-element
        <img
          alt=''
          src={tileOptimizedImage(tile.primary2x3)}
          fetchpriority={isPrioritized ? 'high' : 'auto'} // eslint-disable-line react/no-unknown-property
          loading={isPrioritized ? undefined : 'lazy'}
        />
      }
    />
  )
}

const ClassTileNameplateWithInstructor = () => {
  const { tile, isPrioritized, isSeries } = useClassTile()

  return (
    <Nameplate
      size='small'
      image={
        // explicitly use a standard `img` element so we can deterministically preload images
        // eslint-disable-next-line @next/next/no-img-element
        <img
          alt={isSeries ? tile.title : tile.instructorName ?? tile.title}
          src={nameplateOptimizedImage(tile.nameplateImage)}
          style={{ width: '100%' }}
          fetchpriority={isPrioritized ? 'high' : 'auto'} // eslint-disable-line react/no-unknown-property
          loading={isPrioritized ? undefined : 'lazy'}
        />
      }
      separator={!isSeries}
      subText={!isSeries ? tile.headline : undefined}
    />
  )
}

const ClassTileFooter = () => {
  const { isSeries, showWatchTrailerButton } = useClassTile()

  return (
    <>
      {isSeries && <SeriesLogo width={137} />}

      <ClassTileNameplateWithInstructor />

      {showWatchTrailerButton && <ClassTileWatchTrailer />}

      {!showWatchTrailerButton && <ClassTileDuration />}
    </>
  )
}

const ClassTileDuration = () => {
  const { tile } = useClassTile()
  return (
    <span className='mc-mt-3 mc-text-x-small'>
      {getDuration(tile.link, tile.duration)}
    </span>
  )
}

const ClassTileWatchTrailer = () => {
  const { t } = useTranslation('@mc/next')
  const { showWatchTrailerButton } = useClassTile()
  const { onWatchTrailerClick } = useClassTileActions()

  if (!showWatchTrailerButton) {
    return null
  }

  return (
    <Button
      onClick={onWatchTrailerClick}
      kind='tertiary'
      fullWidth
      className='mc-mt-3 mc-px-3'
    >
      <IconPlayDefault size='sm' className='mc-mr-2' />
      {t('home.trendingCarousel.watchTrailer')}
    </Button>
  )
}

const useClassTileActions = () => {
  const {
    tile,
    carouselSlug,
    location,
    onClick,
    onClickWatchTrailer,
    trackProps,
  } = useClassTile()
  const { openVideo } = useVideoContext()

  const handleLinkClick = () => {
    onClick?.({
      classTitle: tile.title,
      carouselSlug,
      location,
      link: tile.link,
    })
  }

  const handleClickWatchTrailer = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!tile.mediaUuid) {
      return
    }

    trackWatchTrailerClick({
      location,
      category: carouselSlug,
      slug: tile.slug,
      type: tile.type as CourseSubType,
      ...trackProps,
    })

    if (onClickWatchTrailer) {
      onClickWatchTrailer()
    } else {
      openVideo?.(tile)
    }
  }

  return {
    onLinkClick: handleLinkClick,
    onWatchTrailerClick: handleClickWatchTrailer,
  }
}

const ClassTileClickableLink = ({
  openNewPage,
  children,
}: {
  openNewPage: boolean
  children: ReactNode
}) => {
  const { tile } = useClassTile()
  const { onLinkClick } = useClassTileActions()

  const content = <Tile aspectRatio='9x16'>{children}</Tile>

  if (!tile.link) {
    return (
      <div className='d-block mc-corners--sm mc-overflow--hidden'>
        {content}
      </div>
    )
  }

  return (
    <Link
      className='d-block mc-corners--sm mc-overflow--hidden'
      href={tile.link}
      passHref
      newTab={openNewPage}
      onClick={onLinkClick}
    >
      {content}
    </Link>
  )
}

export const ClassTile = ({
  tile,
  carouselSlug,
  location,
  onClick,
  openNewPage = false,
  onClickWatchTrailer,
  clickBehavior = 'watchTrailer',
  isPrioritized = false,
  trackProps,
}: ClassTileProps) => (
  <HoverHandler>
    {({ hovering, focused }) => (
      <ClassTileContext
        carouselSlug={carouselSlug}
        location={location}
        onClick={onClick}
        onClickWatchTrailer={onClickWatchTrailer}
        tile={tile}
        isPrioritized={isPrioritized}
        clickBehavior={clickBehavior}
        trackProps={trackProps}
      >
        <ClassTileClickableLink openNewPage={openNewPage}>
          <ClassTileTop />

          {/* hack to slightly grow the overlay to avoid uncovered sliver (subpixel rendering issue) */}
          <style>{`.mc-4uew086q.mc-tile-overlay { transform: scale(1.01); }`}</style>
          <TileOverlay className='mc-4uew086q' />

          <AnimationHandler type='zoom' animating={hovering || focused}>
            <ClassTileImage />
          </AnimationHandler>

          <TileCaption padding={4} position='center bottom' flush>
            <ClassTileFooter />
          </TileCaption>
        </ClassTileClickableLink>
      </ClassTileContext>
    )}
  </HoverHandler>
)

const ClassTileTop = () => (
  <>
    <TileCaption position='left top' padding={4}>
      <ClassTileTag />
    </TileCaption>
  </>
)
